'use client'

import { Card } from '@acko/card'
import { Badge } from '@acko/badge'
import { Typography } from '@acko/typography'
import { cn } from '@utils/utils'
import Image from 'next/image'
import { useState } from 'react'
import styles from '../page.module.css'
import { trackComprehensiveEngagement } from './ComprehensiveAnalyticsClient'

/* ─── Plan icons (same CDN assets as parent CoveragesSectionClient) ─────────── */
const imgBikeComprehensive = 'https://acko-cms-dev.s3.ap-south-1.amazonaws.com/bike_comprehensive_2b34a13527.svg'
const imgBikeOwnDamage = 'https://acko-cms-dev.s3.ap-south-1.amazonaws.com/bike_own_damage_21b23f9c3d.svg'
const imgBikeThirdParty = 'https://acko-cms-dev.s3.ap-south-1.amazonaws.com/bike_third_party_97045f8d5d.svg'
const imgTickIconBrand = 'https://acko-cms-dev.s3.ap-south-1.amazonaws.com/tick_icon_brand_cc533a7009.svg'
const imgCloseIcon = 'https://acko-cms-dev.s3.ap-south-1.amazonaws.com/not_available_icon_490e879516.svg'

/* ─── Table data ─────────────────────────────────────────────────────────────── */
type CellType = 'yes' | 'no' | 'addon' | 'partial'
type PlanId = 'comp' | 'own' | 'tp'

const PLAN_PRICES: Record<PlanId, string> = {
  comp: '₹475+',
  own: '₹100+',
  tp: '₹457+',
}

type CompareRow = {
  label: string
  comp: CellType
  own: CellType
  tp: CellType
  /* Short label shown on mobile when columns are abbreviated */
  note?: string
}

type CompareSection = {
  heading: string
  rows: CompareRow[]
}

const COMPARE_SECTIONS: CompareSection[] = [
  {
    heading: 'Own bike protection',
    rows: [
      { label: 'Accidental damage to your bike', comp: 'yes', own: 'yes', tp: 'no' },
      { label: 'Theft & burglary', comp: 'yes', own: 'yes', tp: 'no' },
      { label: 'Fire & explosion', comp: 'yes', own: 'yes', tp: 'no' },
      { label: 'Flood, storm & natural calamities', comp: 'yes', own: 'yes', tp: 'no' },
      { label: 'Earthquake & landslide damage', comp: 'yes', own: 'yes', tp: 'no' },
      { label: 'Rat & animal damage to wiring', comp: 'yes', own: 'yes', tp: 'no' },
      { label: 'Damage during transit', comp: 'yes', own: 'yes', tp: 'no' },
    ],
  },
  {
    heading: 'Third-party liability',
    rows: [
      {
        label: 'Bodily injury / death liability (unlimited)',
        comp: 'yes',
        own: 'no',
        tp: 'yes',
      },
      {
        label: 'Third-party property damage',
        comp: 'yes',
        own: 'no',
        tp: 'yes',
      },
    ],
  },
  {
    heading: 'Optional add-ons',
    rows: [
      { label: 'Zero depreciation cover', comp: 'addon', own: 'addon', tp: 'no' },
      { label: 'Engine protection', comp: 'addon', own: 'addon', tp: 'no' },
      { label: 'Return to invoice', comp: 'addon', own: 'addon', tp: 'no' },
      { label: 'Personal accident cover', comp: 'addon', own: 'addon', tp: 'addon' },
      { label: 'Pillion rider cover', comp: 'addon', own: 'addon', tp: 'addon' },
      { label: 'Consumables cover', comp: 'addon', own: 'addon', tp: 'no' },
      { label: 'Helmet protect', comp: 'addon', own: 'addon', tp: 'no' },
      { label: 'NCB Protection', comp: 'addon', own: 'addon', tp: 'no' },
    ],
  },
  {
    heading: 'Premium range',
    rows: [
      {
        label: 'Starting price (approx.)',
        comp: 'yes',
        own: 'yes',
        tp: 'yes',
        note: 'Comp ₹475 · OD ₹100 · TP ₹457',
      },
    ],
  },
]

/* ─── Cell renderer ──────────────────────────────────────────────────────────── */
const Cell = ({ value, note, planId }: { value: CellType; note?: string; planId: PlanId }) => {
  // Desktop only: apply purple background for comprehensive column
  const base = planId === 'comp' ? 'lg:bg-[var(--color-accent-purple-surface)]' : ''

  if (note) {
    return (
      <div className={`flex-1 ${base} flex items-center justify-center py-12 px-8 lg:py-16 lg:px-20`}>
        <Typography scale="xl" emphasis="bold" color="primary" className="text-center">
          {PLAN_PRICES[planId]}
        </Typography>
      </div>
    )
  }

  if (value === 'yes') {
    return (
      <div className={`flex-1 ${base} flex items-center justify-center py-12 px-8 lg:py-16 lg:px-20`}>
        <Image
          alt="Covered"
          className="w-24 h-24 lg:w-28 lg:h-28 object-contain shrink-0"
          src={imgTickIconBrand}
          width={28}
          height={28}
        />
      </div>
    )
  }

  if (value === 'no') {
    return (
      <div className={`flex-1 ${base} flex items-center justify-center py-12 px-8 lg:py-16 lg:px-20`}>
        <Image
          alt="Not covered"
          className="w-24 h-24 lg:w-28 lg:h-28 object-contain shrink-0"
          src={imgCloseIcon}
          width={28}
          height={28}
        />
      </div>
    )
  }

  /* addon */
  return (
    <div className={`flex-1 ${base} flex items-center justify-center py-12 px-8 lg:py-16 lg:px-20`}>
      <Badge color="purple" variant="solid" size="md" textCase="sentence">
        Add-on
      </Badge>
    </div>
  )
}

/* ─── Decision framework card ────────────────────────────────────────────────── */

const BIKE_AGE_OPTIONS = [
  {
    id: 'new',
    label: 'Brand new',
    recommendation:
      'If getting a new bike, opt for full coverage without depreciation and return to invoice. Full coverage without depreciation is less expensive, since parts are most expensive when new, while return-to-invoice will cover the entire on-road cost if the bike is stolen.',
  },
  {
    id: '1-3',
    label: '1-3 year old',
    recommendation:
      "If you have a bike that's 1-3 years old, go for comprehensive coverage, zero depreciation, and engine protection, as there will be depreciation, and engine coverage becomes necessary due to waterlogged roads during the monsoon season.",
  },
  {
    id: '3-5',
    label: '3-5 years old',
    recommendation:
      "One must go for comprehensive cover along with personal accident cover, and if the bike's value exceeds ₹80,000, one can opt for zero depreciation. Zero dep is not mandatory, but it is worth considering, especially if one has a high-end bike.",
  },
  {
    id: '5plus',
    label: 'For older bikes',
    recommendation:
      'Opt for comprehensive cover, including personal accident cover. You need not take zero-depreciation cover, as parts are cheaper now; however, take comprehensive cover, as you need protection against theft and natural calamities. Going with a third party alone is quite risky, even if your bike costs ₹40-60K.',
  },
]

const DecisionCard = () => {
  const [selected, setSelected] = useState<string>('new')
  const active = BIKE_AGE_OPTIONS.find(o => o.id === selected)
  if (!active) return null

  return (
    <Card variant="primary" className="p-20 md:p-24 lg:p-24 flex flex-col gap-20">
      <div className="flex flex-col gap-8">
        <Typography scale="xl" emphasis="bold" as="h3" color="primary">
          Which plan is right for you?
        </Typography>
        <Typography scale="base" color="secondary">
          Select your bike&apos;s age for a personalised recommendation.
        </Typography>
      </div>

      {/*
       * Pill row: overflow-x auto + scrollbarHide on mobile so all 4 options
       * are reachable without layout overflow. Using proper ACKO tab pill pattern.
       */}
      <div className="flex gap-8 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden -mx-2 px-2 flex-wrap md:flex-nowrap">
        {BIKE_AGE_OPTIONS.map(opt => {
          const isActive = selected === opt.id
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => {
                void trackComprehensiveEngagement('plan_recommendation', opt.label)
                setSelected(opt.id)
              }}
              className="px-16 py-10 rounded-full font-medium transition-all duration-200 cursor-pointer whitespace-nowrap shrink-0 border-0"
              style={{
                fontSize: 'var(--font-body-sm-size)',
                lineHeight: 'var(--font-body-sm-line)',
                backgroundColor: isActive ? 'var(--color-accent-purple-surface)' : '#fbfbfb',
                color: isActive ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
                boxShadow: isActive ? 'var(--shadow-xs)' : 'none',
                border: isActive ? '1px solid var(--color-border-brand)' : '1px solid var(--color-border-strong)',
              }}>
              {opt.label}
            </button>
          )
        })}
      </div>

      {/* Recommendation — animated with key re-mount on selection change */}
      <Card variant="ghost" key={selected} className={`flex gap-12 items-start p-16 ${styles.tipInfoSurface}`}>
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          className={`shrink-0 mt-1 ${styles.tipInfoText}`}
          aria-hidden>
          <circle cx="10" cy="10" r="9" stroke="currentColor" strokeWidth="1.5" />
          <path d="M10 6.5v4.5M10 13.5v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
        <Typography scale="base" className={`flex-1 ${styles.tipInfoText}`}>
          {active.recommendation}
        </Typography>
      </Card>
    </Card>
  )
}

/* ─── Main Section ───────────────────────────────────────────────────────────── */

export const ComprehensiveVsThirdPartySectionClient = () => {
  /* Default: Comprehensive + Third-party selected (using correct IDs) */
  const [selectedPlans, setSelectedPlans] = useState<Array<'comp' | 'own' | 'tp'>>(['comp', 'tp'])

  const handlePillClick = (planId: 'comp' | 'own' | 'tp') => {
    setSelectedPlans(current => {
      if (current.includes(planId)) {
        /* Already selected → remove it (cross behaviour) */
        return current.filter(id => id !== planId)
      }
      if (current.length >= 2) {
        /* 2 already selected → do nothing (pill is disabled) */
        return current
      }
      /* Add to selection */
      return [...current, planId]
    })
  }

  const PLAN_OPTIONS = [
    { id: 'comp' as const, label: 'Comprehensive', icon: imgBikeComprehensive },
    { id: 'own' as const, label: 'Own damage', icon: imgBikeOwnDamage },
    { id: 'tp' as const, label: 'Third-party', icon: imgBikeThirdParty },
  ]

  return (
    <div id="vs-third-party" className="flex flex-col gap-32 lg:gap-40 w-full scroll-mt-[104px]">
      {/* Section heading */}
      <div className="flex flex-col gap-8">
        <Typography scale="4xl" emphasis="bold" as="h2" color="primary" className="text-balance">
          Comprehensive vs third-party vs own damage bike insurance
        </Typography>
        <Typography scale="base" color="secondary">
          There are three types of bike insurance policies: comprehensive, third-party, and own damage. Third-party
          covers liability to others, own damage covers your bike, and comprehensive combines both for complete
          protection.
        </Typography>
      </div>

      {/* Comparison table with mobile card behavior */}
      <div className="w-full">
        <Card variant="primary" className="overflow-hidden w-full flex flex-col lg:rounded-[24px]">
          {/* Sticky Header: selector pills (mobile) + column headers */}
          <div className="shrink-0 bg-white rounded-t-[16px] lg:rounded-t-[24px] border-b border-[#ebebeb]">
            {/* Mobile Selector Pills - Only visible on mobile */}
            <div className="block lg:hidden px-18 py-20 border-b border-[#ebebeb]">
              <Typography scale="xl" emphasis="bold" as="h3" color="primary" className="mb-14">
                Select plans to compare
              </Typography>

              {/* Selector Pills */}
              <div className="flex flex-wrap gap-8">
                {PLAN_OPTIONS.map(plan => {
                  const isSelected = selectedPlans.includes(plan.id)
                  const isDisabled = !isSelected && selectedPlans.length >= 2

                  return (
                    <button
                      key={plan.id}
                      type="button"
                      onClick={() => {
                        void trackComprehensiveEngagement('plan_comparison', plan.label)
                        handlePillClick(plan.id)
                      }}
                      disabled={isDisabled}
                      className={cn(
                        'flex items-center gap-6 px-14 py-8 rounded-full border-[1.5px] transition-all duration-200 text-[13px] font-medium',
                        isSelected
                          ? 'bg-[var(--color-accent-purple-surface)] border-[var(--color-border-brand)] text-[var(--color-text-brand)]'
                          : 'bg-[var(--color-surface)] border-[var(--color-border-default)] text-[var(--color-text-secondary)]',
                        isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'
                      )}>
                      <span>{plan.label}</span>
                      {isSelected && (
                        <span className="w-14 h-14 rounded-full bg-[var(--color-fill-subtle-active)] flex items-center justify-center shrink-0">
                          <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                            <path
                              d="M1.5 1.5l5 5M6.5 1.5l-5 5"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                            />
                          </svg>
                        </span>
                      )}
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Column Headers */}
            {/* Desktop: Show all 3 columns */}
            <div className="hidden lg:flex w-full">
              <div className="w-[340px] shrink-0 py-20 px-24" />
              <div className="flex-1 bg-[var(--color-accent-purple-surface)] flex flex-col items-center justify-center py-20 px-24 gap-4">
                <Image
                  alt=""
                  className="w-40 h-40 object-contain shrink-0"
                  src={imgBikeComprehensive}
                  width={40}
                  height={40}
                />
                <Typography scale="base" emphasis="bold" color="primary" className="text-center">
                  Comprehensive
                </Typography>
              </div>
              <div className="flex-1 flex flex-col items-center justify-center py-20 px-24 gap-4">
                <Image
                  alt=""
                  className="w-40 h-40 object-contain shrink-0"
                  src={imgBikeOwnDamage}
                  width={40}
                  height={40}
                />
                <Typography scale="base" emphasis="bold" color="primary" className="text-center">
                  Own damage
                </Typography>
              </div>
              <div className="flex-1 flex flex-col items-center justify-center py-20 px-24 gap-4">
                <Image
                  alt=""
                  className="w-40 h-40 object-contain shrink-0"
                  src={imgBikeThirdParty}
                  width={40}
                  height={40}
                />
                <Typography scale="base" emphasis="bold" color="primary" className="text-center">
                  Third-party
                </Typography>
              </div>
            </div>

            {/* Mobile: Show only selected columns */}
            <div className="flex lg:hidden w-full">
              <div className="w-[40%] shrink-0 py-14 px-12" />
              {selectedPlans.map(planId => {
                const plan = PLAN_OPTIONS.find(p => p.id === planId)
                if (!plan) return null

                return (
                  <div
                    key={planId}
                    className={`flex-1 ${
                      planId === 'comp' ? 'bg-[var(--color-accent-purple-surface)]' : ''
                    } flex flex-col items-center justify-center py-14 px-8 gap-4`}>
                    <Image
                      alt=""
                      className="w-32 h-32 object-contain shrink-0"
                      src={plan.icon}
                      width={32}
                      height={32}
                    />
                    <Typography scale="base" emphasis="bold" color="primary" className="text-center">
                      {plan.label}
                    </Typography>
                  </div>
                )
              })}

              {/* Empty placeholder slots when < 2 selected */}
              {Array.from({ length: 2 - selectedPlans.length }).map((_, i) => (
                <div key={`empty-${i}`} className="flex-1 flex flex-col items-center justify-center py-14 px-8 gap-4">
                  <div className="w-32 h-32 border-[1.5px] border-dashed border-[var(--color-border-medium)] rounded-xl flex items-center justify-center">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M12 5v14M5 12h14"
                        stroke="var(--color-text-disabled)"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>
                  <Typography scale="base" color="secondary" className="text-center">
                    Pick a plan
                  </Typography>
                </div>
              ))}
            </div>
          </div>

          {/* Table body */}
          <div className="flex-1">
            {/* Sections */}
            {COMPARE_SECTIONS.map((section, si) => (
              <div key={section.heading}>
                {/* Section heading */}
                <div className="flex w-full">
                  <div className="w-full py-10 px-12 lg:py-12 lg:px-24 bg-[var(--color-surface-subtle,#f5f5f5)]">
                    <Typography scale="xs" emphasis="bold" color="secondary" className="uppercase tracking-[1.5px]">
                      {section.heading}
                    </Typography>
                  </div>
                </div>

                {/* Desktop Rows: Show all 3 columns */}
                <div className="hidden lg:block">
                  {section.rows.map((row, ri) => (
                    <div key={row.label}>
                      <div className="flex w-full">
                        {/* Feature label */}
                        <div className="w-[340px] shrink-0 flex items-center py-16 px-24">
                          <Typography scale="base" color="primary">
                            {row.label}
                          </Typography>
                        </div>
                        {/* Plan cells */}
                        <Cell value={row.comp} note={row.note} planId="comp" />
                        <Cell value={row.own} note={row.note} planId="own" />
                        <Cell value={row.tp} note={row.note} planId="tp" />
                      </div>
                      {ri < section.rows.length - 1 && <div className="h-px bg-[#ebebeb] mx-24" />}
                    </div>
                  ))}
                </div>

                {/* Mobile Rows: Show only selected columns */}
                <div className="block lg:hidden">
                  {section.rows.map((row, ri) => (
                    <div key={row.label}>
                      <div className="flex w-full">
                        {/* Feature label */}
                        <div className="w-[40%] shrink-0 flex items-center py-14 px-12">
                          <Typography scale="base" color="primary">
                            {row.note ? <span className="block">Premium range</span> : row.label}
                          </Typography>
                        </div>

                        {/* Dynamic columns based on selection */}
                        {selectedPlans.map(planId => {
                          const value = row[planId]
                          return (
                            <div
                              key={planId}
                              className={`flex-1 ${
                                planId === 'comp' ? 'bg-[var(--color-accent-purple-surface)]' : ''
                              } flex items-center justify-center py-14 px-8`}>
                              {/* Mobile: Simple content without Cell wrapper */}
                              {row.note ? (
                                <Typography scale="xl" emphasis="bold" color="primary" className="text-center">
                                  {PLAN_PRICES[planId]}
                                </Typography>
                              ) : value === 'yes' ? (
                                <Image
                                  alt="Covered"
                                  className="w-24 h-24 object-contain shrink-0"
                                  src={imgTickIconBrand}
                                  width={24}
                                  height={24}
                                />
                              ) : value === 'no' ? (
                                <Image
                                  alt="Not covered"
                                  className="w-24 h-24 object-contain shrink-0"
                                  src={imgCloseIcon}
                                  width={24}
                                  height={24}
                                />
                              ) : (
                                <Badge color="purple" variant="solid" size="md" textCase="sentence">
                                  Add-on
                                </Badge>
                              )}
                            </div>
                          )
                        })}

                        {/* Empty columns when < 2 selected */}
                        {Array.from({ length: 2 - selectedPlans.length }).map((_, i) => (
                          <div
                            key={`empty-${i}`}
                            className="flex-1 flex items-center justify-center py-14 px-8 text-gray-400">
                            <span className="text-2xl">—</span>
                          </div>
                        ))}
                      </div>
                      {ri < section.rows.length - 1 && <div className="h-px bg-[#ebebeb] mx-12" />}
                    </div>
                  ))}
                </div>

                {si < COMPARE_SECTIONS.length - 1 && <div className="h-px bg-[#ebebeb] w-full" />}
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Decision framework card */}
      <DecisionCard />
    </div>
  )
}
